import { memo, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';

import { Account, Repository } from 'src/components/pipelines/models';

import {
  DOCS_CONFIGURE_CLONE_DEPTH,
  DOCS_DEPLOYMENTS,
  DOCS_SERVICE_CONTAINERS,
  DOCS_SETUP_DEPLOYMENT_GATING,
  DOCS_TROUBLESHOOTING_RUNNERS_URL,
  DOCS_YML_DOCKER_URL,
  DOCS_YML_URL,
  MEMORY_LIMIT_EXCEEDED_STEP_URL,
  SUPPORT_PORTAL,
  VALIDATOR_URL,
  YML_FILE_NAME,
} from '../../constants';
import { requestBuildConfiguration } from '../../redux/actions/pipelines';
import { getYmlPath } from '../../redux/selectors/pipelines';
import {
  getAddConfigurationURL,
  getDeploymentSettingsPageURL,
} from '../../utils/links';

export interface Props {
  account: Account;
  repository: Repository;
  errorKey: string;
  revision: string;
  isFailedSelfHosted?: boolean;
}

const Link = ({ href, text }: { href: string; text: any }) => (
  <Button href={href} spacing="none" appearance="link" target="_blank">
    {text}
  </Button>
);

const WarningLinks = ({
  account,
  repository,
  errorKey,
  revision,
  isFailedSelfHosted,
}: Props) => {
  const dispatch = useDispatch();

  const ymlPath = useSelector(getYmlPath);

  useEffect(() => {
    if (revision) {
      dispatch(requestBuildConfiguration(revision));
    }
  }, [revision, dispatch]);

  const documentationLinkData = useMemo(() => {
    const docLinks = [
      {
        key: '',
        text: 'See our documentation',
        href: DOCS_YML_URL,
        actionSubjectId: 'documentationLink',
      },
      {
        key: 'plan-service.parse.invalid-image-name',
        text: 'See our documentation',
        href: DOCS_YML_DOCKER_URL,
        actionSubjectId: 'dockerDocumentationLink',
      },
      {
        key: 'plan-service.parse.too-many-services',
        text: 'Read more about service limits',
        href: DOCS_SERVICE_CONTAINERS,
        actionSubjectId: 'serviceContainersDocumentationLink',
      },
      {
        key: 'agent-service.account-concurrency.reached',
        text: 'Let us know if we were wrong',
        href: SUPPORT_PORTAL,
        actionSubjectId: 'supportAccountLink',
      },
      {
        key: 'agent.git.git-object-not-found',
        text: 'Read more about Git clone depth',
        href: DOCS_CONFIGURE_CLONE_DEPTH,
        actionSubjectId: 'gitCloneDepthDocumentationLink',
      },
      {
        keyStartsWith: 'plan-service.parse',
        excludeKeys: ['invalid-deployment'],
        text: `Validate your ${YML_FILE_NAME}`,
        href: VALIDATOR_URL,
        actionSubjectId: 'validatorLink',
      },
      {
        key: 'plan-service.parse.invalid-deployment',
        text: `Read more about deployments`,
        href: DOCS_DEPLOYMENTS,
        actionSubjectId: 'documentationLink',
      },
      {
        key: 'result-service.deployment-gate.deployment-gate-checks-suite-invalid-state',
        text: `Read more about deployment gating`,
        href: DOCS_SETUP_DEPLOYMENT_GATING,
        actionSubjectId: 'documentationLink',
      },
      {
        key: 'result-service.deployment-gate.deployment-gate-checks-failed-error',
        text: `Read more about deployment gating`,
        href: DOCS_SETUP_DEPLOYMENT_GATING,
        actionSubjectId: 'documentationLink',
      },
      repository.userIsAdmin
        ? {
            key: 'plan-service.parse.invalid-deployment',
            text: `View deployment settings`,
            href: getDeploymentSettingsPageURL(
              repository.path,
              account.addonKey
            ),
            actionSubjectId: 'deploymentSettings',
          } // tslint:disable-line
        : ({} as any),
    ];

    docLinks.push({
      key: 'agent.step.memory-limit-exceeded',
      text: `Troubleshooting Steps`,
      href: MEMORY_LIMIT_EXCEEDED_STEP_URL,
      actionSubjectId: 'serviceContainersMemoryDocumentationLink',
    });

    return docLinks;
  }, [repository, account]);

  const documentationLinks = useMemo(() => {
    if (isFailedSelfHosted) {
      return [
        {
          key: '',
          text: 'Troubleshooting runners',
          href: DOCS_TROUBLESHOOTING_RUNNERS_URL,
          actionSubjectId: 'troubleshootingSelfHostedRunners',
        },
      ];
    }
    const findLinks = (link: any) =>
      link.key === errorKey ||
      (link.keyStartsWith && errorKey.startsWith(link.keyStartsWith));
    // we don't want to show link to external validator in certain cases
    const excludeLinks = (link: any) =>
      Array.isArray(link.excludeKeys)
        ? !link.excludeKeys.some((key: any) => errorKey.indexOf(key) !== -1)
        : true;
    const links = documentationLinkData.filter(findLinks).filter(excludeLinks);
    return links.length ? links : [documentationLinkData[0]];
  }, [errorKey, documentationLinkData, isFailedSelfHosted]);

  return (
    <>
      {documentationLinks.map((link, key) => (
        <Box
          key={`documentation_link_${key}`}
          xcss={xcss({ marginBottom: 'space.050' })}
        >
          <Link key={link.key} href={link.href} text={link.text} />
        </Box>
      ))}
      {errorKey.indexOf('file-not-found') !== -1 ? (
        <Link
          text={`Add ${YML_FILE_NAME}`}
          href={getAddConfigurationURL(repository.path, revision)}
        />
      ) : (
        <Link text={`View ${YML_FILE_NAME}`} href={ymlPath} />
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(WarningLinks);
